import { Box, Text } from "theme-ui";
import { useState } from "react";
import { useDispatch } from "react-redux";
import filter from "lodash/fp/filter";
import flow from "lodash/fp/flow";
import get from "lodash/fp/get";
import keys from "lodash/fp/keys";
import map from "lodash/fp/map";
import pick from "lodash/fp/pick";
import some from "lodash/fp/some";

import { updateUserChoiceValues } from "store/app/appSlice";
import { useTranslation } from "hooks";
import { Button, ImageCarouselSelectGoalGear } from "components";
import ModalCenterEnlarged from "../ModalCenterEnlarged";
import {
  BlockerIcon,
  BlockerIconGrey,
  CheckmarkIcon,
  GloveIcon,
  GloveIconGrey,
  PadsIcon,
  PadsIconGrey,
} from "components/icons";
import { DropDown } from "components/Forms";
import * as styles from "./ModalSelectGoalGear.styles";

export default function ModalSelectGoalGear({
  isTopMostModal,
  onClose,
  customisation,
}) {
  const { t } = useTranslation();

  const dispatch = useDispatch();

  // Defines what Goalie Products the user has selected to cutomise, (e.g Gloves and Blocker only)
  const initialUserChoices = customisation.fields.reduce(
    (acc, curr) => ({
      ...acc,
      [curr.name]: curr.defaultValue,
    }),
    {}
  );

  const [formValues, setFormValues] = useState({
    ...initialUserChoices,
  });

  const dispatchUpdateUserChoiceValues = (fieldValue) => {
    dispatch(updateUserChoiceValues(fieldValue));
  };

  const userGoalGearChoiceValuesToSubmit = (values) =>
    flow(
      keys,
      map((field) => ({
        name: field,
        value: values[field],
      }))
    )(values);

  const handleSubmit = (evt) => {
    evt.preventDefault();
    flow(
      userGoalGearChoiceValuesToSubmit,
      dispatchUpdateUserChoiceValues,
      onClose
    )(formValues);
  };

  function getGoalGearIcon({ iconName, isSelected }) {
    switch (iconName) {
      case "pads-icon":
        return isSelected ? <PadsIcon /> : <PadsIconGrey />;
      case "glove-icon":
        return isSelected ? <GloveIcon /> : <GloveIconGrey />;
      case "blocker-icon":
        return isSelected ? <BlockerIcon /> : <BlockerIconGrey />;
      default:
        return;
    }
  }

  function getEquipmentOptionsSelected() {
    const customisationFields = customisation.fields.filter(
      ({ name }) => !["full-right-hand", "set-sizing"].includes(name)
    );
    const selectedOptionTextArray = flow(
      filter(({ name }) => formValues[name]),
      map(get("label"))
    )(customisationFields);
    if (selectedOptionTextArray.length === 0) {
      return;
    }
    if (selectedOptionTextArray.length === customisationFields.length) {
      return t("your full set");
    }
    return `${t("your")} ${selectedOptionTextArray.join(" & ")}`;
  }

  function transitionToggleThumb(isSelected) {
    if (isSelected) {
      return {
        left: "34px",
      };
    }
  }

  return (
    <>
      <ModalCenterEnlarged
        isTopMostModal={isTopMostModal}
        onClose={onClose}
        disableEscapeKey
        background={"background"}
        boxShadow={"0px 5px 10px rgba(0, 0, 0, 0.375)"}
      >
        <Box as="form" onSubmit={handleSubmit} sx={styles.contentContainer}>
          <Text variant="h4Light" sx={styles.modalTitle}>
            {t(customisation.modalTitle)}
          </Text>

          <Box sx={styles.gearSelectorContainer}>
            {customisation.fields
              .filter(({ component }) => component === "Checkbox")
              .map(
                ({ label, name, checkedValue, uncheckedValue, iconName }) => {
                  const isSelected = formValues[name] === checkedValue;
                  return (
                    <Button
                      key={name}
                      variant="unstyled"
                      sx={styles.gearItemOption({ isSelected })}
                      onClick={() => {
                        setFormValues((prevState) => ({
                          ...prevState,
                          [name]: !isSelected ? checkedValue : uncheckedValue,
                        }));
                      }}
                      iconAfter={
                        <Box sx={styles.checkbox({ isSelected })}>
                          {isSelected && <CheckmarkIcon />}
                        </Box>
                      }
                    >
                      <Box sx={styles.iconAndText}>
                        {getGoalGearIcon({ iconName, isSelected })}
                        <Text variant="h5Light">{label}</Text>
                      </Box>
                    </Button>
                  );
                }
              )}
          </Box>

          <Box sx={styles.toggleAndSubmitContainer}>
            {customisation.fields
              .filter(({ component }) => component === "Dropdown")
              .map(({ name, label, options, defaultValue }) => {
                return (
                  <Box sx={styles.dropdownContainer} key={name}>
                    <Text variant="h5Light" sx={{}}>
                      {label}
                    </Text>
                    <DropDown
                      name={name}
                      options={options}
                      value={formValues[name] || defaultValue}
                      size={"medium"}
                      onChange={(value) => {
                        return setFormValues((prevState) => ({
                          ...prevState,
                          [name]: value,
                        }));
                      }}
                    ></DropDown>
                  </Box>
                );
              })}
            {customisation.fields
              .filter(({ component }) => component === "Toggle")
              .map(
                ({
                  checkedLabel,
                  uncheckedLabel,
                  checkedValue,
                  uncheckedValue,
                  name,
                }) => {
                  const isSelected = formValues[name] === checkedValue;
                  const isDisabled =
                    formValues["customise-glove"] === false &&
                    formValues["customise-blocker"] === false;
                  return (
                    <Box key={name} sx={styles.toggleSwitchContainer}>
                      <Text
                        sx={styles.handText({ isDisabled })}
                        variant="h5Light"
                      >
                        {uncheckedLabel}
                      </Text>
                      <Box
                        as="button"
                        type="button"
                        onClick={() => {
                          setFormValues((prevState) => ({
                            ...prevState,
                            [name]: !isSelected ? checkedValue : uncheckedValue,
                          }));
                        }}
                        sx={styles.toggleSwitch({ isDisabled })}
                        disabled={isDisabled}
                      >
                        <Box
                          className="thumb"
                          style={transitionToggleThumb(isSelected)}
                        >
                          <CheckmarkIcon />
                        </Box>
                      </Box>
                      <Text
                        sx={styles.handText({ isDisabled })}
                        variant="h5Light"
                      >
                        {checkedLabel}
                      </Text>
                    </Box>
                  );
                }
              )}
            <Button
              type="submit"
              variant="primary"
              size="medium"
              isRound
              theme="dark"
              disabled={
                !flow(
                  // needed to exclude toggle from disabled check
                  pick([
                    "customise-pads",
                    "customise-glove",
                    "customise-blocker",
                  ]),
                  some((val) => val)
                )(formValues)
              }
            >
              {t("customize ")}
              {getEquipmentOptionsSelected({
                customisation,
              })}
            </Button>
          </Box>
        </Box>

        <Box sx={styles.modalImageContainer}>
          <ImageCarouselSelectGoalGear
            imageContent={customisation?.equipmentImages}
            modalHeight={customisation?.modalHeight}
            textBoxContent={customisation?.textBoxContent}
          />
        </Box>
      </ModalCenterEnlarged>
    </>
  );
}
